import React, { useState } from 'react';
import CameraFrame from '../../components/ui/CameraFrame';
import { activeProject } from '../../data/mockData';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const { FiCheckSquare, FiSquare, FiImage, FiCrosshair, FiSun } = FiIcons;

const ShotList = () => {
  const [shots, setShots] = useState(activeProject.shots);

  const toggleStatus = (id) => {
    setShots(shots.map(shot => {
      if (shot.id === id) {
        const nextStatus = shot.status === 'done' ? 'pending' : (shot.status === 'pending' ? 'in-progress' : 'done');
        return { ...shot, status: nextStatus };
      }
      return shot;
    }));
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'done': return 'text-green-500 border-green-500/30 bg-green-500/10';
      case 'in-progress': return 'text-yellow-500 border-yellow-500/30 bg-yellow-500/10';
      default: return 'text-[#555] border-[#333] bg-transparent';
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex justify-between items-center mb-6 border-b border-[#222] pb-4">
        <h3 className="font-mono text-lg text-white tracking-widest uppercase">Master Shot List</h3>
        <button className="px-4 py-2 border border-[#333] text-pink-500 font-mono text-xs uppercase hover:bg-pink-500/10 transition-colors">
          + Add Shot
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <AnimatePresence>
          {shots.map((shot) => (
            <motion.div
              key={shot.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <CameraFrame 
                className={`transition-all duration-300 ${shot.status === 'in-progress' ? 'border-pink-500/50' : ''}`}
                active={shot.status === 'in-progress'}
              >
                <div className="p-4 flex flex-col md:flex-row gap-6 items-start md:items-center bg-[#111]">
                  
                  {/* Status Toggle */}
                  <button 
                    onClick={() => toggleStatus(shot.id)}
                    className="flex-shrink-0 focus:outline-none"
                  >
                    {shot.status === 'done' ? (
                      <SafeIcon icon={FiCheckSquare} className="w-6 h-6 text-green-500" />
                    ) : shot.status === 'in-progress' ? (
                      <div className="w-6 h-6 border-2 border-pink-500 rounded-sm animate-pulse flex items-center justify-center">
                        <div className="w-2 h-2 bg-pink-500 rounded-sm" />
                      </div>
                    ) : (
                      <SafeIcon icon={FiSquare} className="w-6 h-6 text-[#444] hover:text-pink-500 transition-colors" />
                    )}
                  </button>

                  {/* Info */}
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs text-[#555] bg-[#0a0a0a] px-2 py-1 border border-[#222]">{shot.id}</span>
                      <h4 className={`text-lg font-bold tracking-wide ${shot.status === 'done' ? 'text-[#666] line-through' : 'text-white'}`}>
                        {shot.title}
                      </h4>
                      <span className={`font-mono text-[10px] uppercase px-2 py-0.5 border ${getStatusColor(shot.status)}`}>
                        {shot.status}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm font-mono text-[#888]">
                      <span className="flex items-center gap-2"><SafeIcon icon={FiCrosshair} className="text-[#555]" /> {shot.angle}</span>
                      <span className="flex items-center gap-2"><SafeIcon icon={FiSun} className="text-[#555]" /> {shot.lighting}</span>
                    </div>
                  </div>

                  {/* Reference Image Thumbnail */}
                  <div className="w-24 h-24 flex-shrink-0 border border-[#333] bg-[#1a1a1a] flex items-center justify-center overflow-hidden relative group">
                    {shot.image ? (
                      <>
                        <img src={shot.image} alt="ref" className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" />
                        <div className="absolute inset-0 bg-pink-500/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                           <SafeIcon icon={FiImage} className="text-white" />
                        </div>
                      </>
                    ) : (
                      <span className="text-[#333] text-xs font-mono">NO REF</span>
                    )}
                  </div>
                </div>
              </CameraFrame>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ShotList;